import Backbone from 'backbone'
import template from './template-naming-form.hbs'
import i18n from 'scoreboard/components/i18n'
import TemplateNamingPanel from './template-naming-panel'
import _ from 'lodash'
import FormView from 'scoreboard/components/form-view'
import paste from 'scoreboard/components/paste/paste'
import metadata from 'scoreboard/components/metadata'
import conductor from 'scoreboard/components/conductor'
import highlight from 'scoreboard/components/highlight'

// the sections for the different types of domain objects that can have
// prefixes and suffixes defined
var ORG_SECTIONS = ['organizations', 'scorecards', 'dashboards', 'reports']

/**
 * Step 2 when creating an organization from a template.  In this step the user
 * can choose any prefixes or suffixes they want for organizations, scorecards,
 * dashboards or reports.
 */
var TemplateNamingForm = Backbone.View.extend({

	// this.model is the domain object that will be the template
	// this.options.orgId is the ID of the org chosen on org-template-destination-form.js
	// this.options.new is true if we're creating a new copy

	tagName: 'form',

	className: 'template-naming-form',

	/**
	 * The form for setting naming conventions of an organization's units
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 * @param {Organization} options.model
	 * @param {Dataset} options.dataset
	 * @param {Organization[]} options.organizations
	 * @param {string} [options.section] (organizations)
	 * @param {string} options.label the name of the organization
	 * @param {boolean} options.useDatasetRanges
	 * @param {boolean} options.allowRecordlessCopies
	 * @param {boolean} options.copyOwners
	 * @param {boolean} options.copyUpdaters
	 * @param {boolean} options.copyViewOrgPermissions
	 * @param {boolean} options.copyRelatedItems
	 */
	initialize: function(options) {
		this.options = options || {}
		if (!this.options.datasetFieldId) {
			this.options.datasetFieldId = this.model.get('datasetFieldId')
		}
		this.panels = []
		this.formView = new FormView({
			el: this.el,
			allowCleanSubmission: true,
			submit: this.submit.bind(this)
		})
	},

	render: function() {
		this.$el.empty().append(template(this.context()))

		// add panels for orgs, scorecards, dashboards, reports (only those
		// appropriate for the type of domain object)
		var content = this.$('.dialog-body-content')
		_.each(this._sections(), function(section) {
			if (this.options.copyType === 'dataset' && section === 'scorecards') {
				let instructions1 = i18n.get('template.paste.suffixInstructions.datasetDomainObjects1')
				let instructions2 = i18n.get('template.paste.suffixInstructions.datasetDomainObjects2')
				content.append(`<div class="banner"><div>${instructions1}</div><div class="paragraph">${instructions2}</div></div>`)
			}

			let placeholders
			if (this.options.copyType === 'dataset' && section !== 'organizations') {
				placeholders = [ {
					placeholder: i18n.get('template.valuePlaceholderTag'),
					value: i18n.get('template.valuePlaceholderValue')
				} ]
			}

			var options = {
				section: section,
				header: section,
				copyType: this.options.copyType,
				placeholders: placeholders
			}

			_.extend(options, this.options[section])

			if (section === 'organizations') {
				options.label = this.options.label
				if (this.options.copyType === 'single') {
					options.heading = i18n.get('template.paste.suffix.header.organization')
				}
			}

			if (this.options.section == section) {
				options.rollupTreeGroupLabel = this.options.copyType === 'dataset'
					? i18n.get('template.valuePlaceholderValue')
					: this.options.rollupTreeGroupLabel
			}

			if (!options.rollupTreeGroupLabel) {
				options.rollupTreeGroupLabel = i18n.get(metadata[section].singularCode)
			}

			var panel = new TemplateNamingPanel(options).render()

			this.panels.push(panel)
			content.append(panel.el)
		}.bind(this))

		this.formView.start()

		// will be null when renaming an org that is already part of a template
		if (this.options.wizard) {
			// clear out any existing buttons and replace them with wizard buttons
			this.$('.dialog-footer').empty()

			this.options.wizard.render({
				formView: this.formView,
				doneKey: this.options.new ? 'common.copy' : 'common.save',
				stateSaver: this.saveState.bind(this)
			})
		}

		this.$('input').first().focus()

		return this
	},

	context: function() {
		let headingKey
		let label
		let primaryButtonLabel = 'common.save'
		if (this.options.new) {
			label = this.options.label
			headingKey = 'template.useHeading'
		} else {
			if (this.model.isCopy()) {
				if (this.model.isDatasetCopy()) {
					headingKey = 'template.editTemplate.plusName'
					label = this.options.organizations.templateLabel
				} else {
					headingKey = 'template.editTemplatedCopy.plusName'
					label = this.options.label
				}
			} else if (this.options.datasetTemplate) {
				headingKey = 'template.editTemplate.plusName'
				label = this.options.label
			} else {
				label = this.options.label
				headingKey = 'common.rename2'
				primaryButtonLabel = 'common.rename'
			}
		}

		var styledLabel = '<q>' + _.escape(label) + '</q>'

		let instructionsCode
		let basedOnDatasetBanner
		if (this.options.copyType === 'dataset') {
			instructionsCode = 'template.paste.suffixInstructions.datasetOrganizations'
			const datasetField = this.options.dataset
				.combinedFieldsById[this.options.datasetFieldId]
			const datasetFieldName = _.get(datasetField, 'label')
			const datasetName = _.get(this.options.dataset, 'attributes.label')

			// #28849 show the dataset name and field used for the template
			// #32026 but only show when editing an existing template; when creating
			// a new template, they just selected it and can see it by hitting Back
			if (datasetFieldName && datasetName && !this.options.new) {
				basedOnDatasetBanner = i18n.get('template.editTemplate.basedOnDataset',
					highlight(datasetFieldName),
					'<a class="sp-navigate text-link" ' +
					`href="/datasets/${this.options.dataset.id}">` +
					`${_.escape(datasetName)}</a>`)
			}
		} else {
			instructionsCode = 'template.paste.suffixInstructions.' + this.options.section
		}

		return {
			heading: i18n.get(headingKey, styledLabel),
			primaryButtonLabel: i18n.get(primaryButtonLabel),
			instructions: i18n.get(instructionsCode),
			basedOnDatasetBanner
		}
	},

	_sections: function() {
		return this.options.section === 'organizations' ?
				ORG_SECTIONS : [ this.options.section ]
	},

	saveState: function() {
		this._recordOptions(this.options)
	},

	_recordOptions: function(options) {
		_.each(this.panels, function(panel) {
			var section = panel.options.section
			if (section === 'strategyMaps') {
				// strategy maps and dashboards share the same prefix/suffix, so if we're on a form showing strategy map
				// info (like creating an individual strategy map template), set the section to 'dashboards' instead
				section = 'dashboards'
			}

			options[section] = panel.info()
		})

		if (options.organizations && options.organizations.label) {
			options.label = options.organizations.label
		}
	},

	submit: function(e) {
		var options = {
			mode: 'copy',
			section: this.options.section,
			id: this.model.id,
			orgId: this.options.orgId,
			index: this.options.index,
			template: true,
			entireTree: this.options.copyType == 'tree',
			datasetFieldId: this.options.datasetFieldId,
			templateFilters: this.options.templateFilters,
			useDatasetRanges: this.options.useDatasetRanges,
			allowRecordlessCopies: this.options.allowRecordlessCopies,
			copyOwners: this.options.copyOwners,
			copyUpdaters: this.options.copyUpdaters,
			copyViewOrgPermissions: this.options.copyViewOrgPermissions,
			copyRelatedItems: this.options.copyRelatedItems
		}

		if (this.options.section === 'organizations') {
			options.parentId = this.options.orgId
			options.organizationTemplatePermissions =
				this.options.organizationTemplatePermissions?.toJSON()
		} else {
			options.parentId = this.options.parentId
		}

		this._recordOptions(options)

		if (options.error == null) {
			options.error = response => {
				this.formView.error(response)
			}
		}

		return paste(options, function (ids) {
			this.trigger('done')
			var copy = this.model.clone()
			let section = this.model.section
			if (section === 'strategyMaps') {
				// #16091 strategyMaps section was set to 'dashboards' in _recordOptions()
				section = 'dashboards'
			}
			var prefixSuffix = options[section]
			var label = prefixSuffix.prefix + this.model.get('label') +
				prefixSuffix.suffix
			copy.set({
				id: ids[this.model.id],
				rootId: ids[this.model.id],
				label: label,
				templateId: this.model.id,
				organizationId: this.options.orgId,
				parentId: options.parentId
			})
			// organization templates do copies asynchronously, so we won't be
			// able to update the tree right away
			if (copy.id) {
				let copyOptions = _.extend({}, options, { label: copy.get('label') })
				Backbone.trigger('copy:domain-object', copyOptions, ids)
			}
			if (this.model.section !== 'organizations') {
				conductor.edit(this.model.section, copy.id)
			}
		}.bind(this))
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this.panels, 'remove')
		_.invokeMap([this.formView, this.wizardProgress], 'remove')
	}

})

export default TemplateNamingForm

TemplateNamingForm.__filename__ = 'templates/template-naming-form.js'
